/**
 * Stocktake tab of the inventory page (#3031): the list of planned and ad hoc
 * stocktake sessions, creating one, and opening a session's counting view.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useWarehouses } from '@/hooks/useInventory';
import { useNotify } from '@/stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import {
  STATUS_COLOR,
  STOCKTAKE_STATUSES,
  apiErrorCode,
  isCalendarDate,
  useCreateStocktake,
  useStocktakeSessions,
  type StocktakeKind,
  type StocktakeSession,
  type StocktakeStatus,
} from './stocktakeSessions';
import StocktakeSessionView from './StocktakeSessionView';

function NewStocktakeDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (session: StocktakeSession) => void;
}) {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const warehousesQuery = useWarehouses();
  const create = useCreateStocktake();
  const warehouses = useMemo(
    () => (warehousesQuery.data?.data ?? []).filter((w) => w.warehouseType === 'core'),
    [warehousesQuery.data],
  );
  const [name, setName] = useState('');
  const [kind, setKind] = useState<StocktakeKind>('ad_hoc');
  const [plannedFor, setPlannedFor] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [binLocationId, setBinLocationId] = useState('');

  const bins = warehouses.find((w) => w.id === warehouseId)?.binLocations ?? [];
  const valid = name.trim() !== '' && warehouseId !== '' && (kind === 'ad_hoc' || isCalendarDate(plannedFor));

  const submit = () => {
    create.mutate(
      {
        name: name.trim(),
        kind,
        warehouseId,
        binLocationId: binLocationId === '' ? null : binLocationId,
        ...(kind === 'planned' ? { plannedFor } : {}),
      },
      {
        onSuccess: (session) => {
          notify.success(t('stocktakeSessions.created'), t('stocktakeSessions.createdDesc', { name: session.name }));
          setName('');
          setPlannedFor('');
          onCreated(session);
        },
        onError: (error) => {
          const code = apiErrorCode(error);
          notify.error(
            tc('error'),
            code === 'STOCKTAKE_SCOPE_TOO_LARGE'
              ? t('stocktakeSessions.errors.scopeTooLarge')
              : t('stocktakeSessions.errors.createFailed'),
          );
        },
      },
    );
  };

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="sm" fullWidth mobileTitle={t('stocktakeSessions.newTitle')}>
      <DialogTitle>{t('stocktakeSessions.newTitle')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
          <Alert severity="info">{t('stocktakeSessions.newInfo')}</Alert>
          <TextField
            autoFocus
            label={t('stocktakeSessions.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 200 } }}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="stocktake-kind-label">{t('stocktakeSessions.kind')}</InputLabel>
            <Select
              labelId="stocktake-kind-label"
              label={t('stocktakeSessions.kind')}
              value={kind}
              onChange={(e) => setKind(e.target.value as StocktakeKind)}
            >
              <MenuItem value="ad_hoc">{t('stocktakeSessions.kinds.ad_hoc')}</MenuItem>
              <MenuItem value="planned">{t('stocktakeSessions.kinds.planned')}</MenuItem>
            </Select>
          </FormControl>
          {kind === 'planned' && (
            <TextField
              type="date"
              label={t('stocktakeSessions.plannedFor')}
              value={plannedFor}
              onChange={(e) => setPlannedFor(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
          )}
          <FormControl fullWidth>
            <InputLabel id="stocktake-warehouse-label">{t('stocktakeSessions.warehouse')}</InputLabel>
            <Select
              labelId="stocktake-warehouse-label"
              label={t('stocktakeSessions.warehouse')}
              value={warehouseId}
              onChange={(e) => {
                setWarehouseId(e.target.value);
                setBinLocationId('');
              }}
            >
              {warehouses.map((warehouse) => (
                <MenuItem key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth disabled={warehouseId === ''}>
            <InputLabel id="stocktake-bin-label">{t('stocktakeSessions.bin')}</InputLabel>
            <Select
              labelId="stocktake-bin-label"
              label={t('stocktakeSessions.bin')}
              value={binLocationId}
              onChange={(e) => setBinLocationId(e.target.value)}
              displayEmpty
              notched
            >
              <MenuItem value="">{t('stocktakeSessions.wholeWarehouse')}</MenuItem>
              {bins.map((bin) => (
                <MenuItem key={bin.id} value={bin.id}>
                  {bin.code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          {tc('cancel')}
        </Button>
        <Button variant="contained" onClick={submit} disabled={!valid || create.isPending}>
          {kind === 'ad_hoc' ? t('stocktakeSessions.createAndStart') : t('stocktakeSessions.create')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}

export default function StocktakeTab() {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const canAdjust = useAuthStore((s) => s.hasPermission('inventory.adjust'));
  const [status, setStatus] = useState<StocktakeStatus | 'all'>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const warehousesQuery = useWarehouses();
  const sessionsQuery = useStocktakeSessions(status);

  if (selectedId) {
    return <StocktakeSessionView sessionId={selectedId} onBack={() => setSelectedId(null)} />;
  }

  const warehouseName = (id: string) => warehousesQuery.data?.data.find((w) => w.id === id)?.name ?? '';
  const sessions = sessionsQuery.data?.data ?? [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography variant="h6">{t('stocktakeSessions.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('stocktakeSessions.subtitle')}
          </Typography>
        </Box>
        {canAdjust && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewOpen(true)}>
            {t('stocktakeSessions.new')}
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }} role="group" aria-label={t('stocktakeSessions.filterByStatus')}>
        {(['all', ...STOCKTAKE_STATUSES] as const).map((value) => (
          <Chip
            key={value}
            label={value === 'all' ? t('stocktakeSessions.statusAll') : t(`stocktakeSessions.status.${value}`)}
            color={status === value ? 'primary' : 'default'}
            variant={status === value ? 'filled' : 'outlined'}
            onClick={() => setStatus(value)}
            aria-pressed={status === value}
          />
        ))}
      </Box>

      {sessionsQuery.isLoading && <LinearProgress aria-label={tc('loading')} />}
      {sessionsQuery.isError && (
        <Alert severity="error" action={<Button onClick={() => void sessionsQuery.refetch()}>{tc('errors.refresh')}</Button>}>
          {t('stocktakeSessions.errors.loadFailed')}
        </Alert>
      )}
      {sessionsQuery.isSuccess && sessions.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('stocktakeSessions.empty')}</Typography>
        </Paper>
      )}

      <Box sx={{ display: 'grid', gap: 1.5 }}>
        {sessions.map((session) => (
          <Paper
            key={session.id}
            variant="outlined"
            component="button"
            type="button"
            onClick={() => setSelectedId(session.id)}
            sx={{ p: 2, textAlign: 'left', cursor: 'pointer', bgcolor: 'background.paper', font: 'inherit', color: 'inherit', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {session.name}
              </Typography>
              <Chip size="small" variant="outlined" label={t(`stocktakeSessions.kinds.${session.kind}`)} />
              <Chip size="small" color={STATUS_COLOR[session.status]} label={t(`stocktakeSessions.status.${session.status}`)} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {warehouseName(session.warehouseId)}
              {session.plannedFor ? ` · ${t('stocktakeSessions.plannedForShort', { date: session.plannedFor })}` : ''}
              {` · ${new Date(session.createdAt).toLocaleDateString()}`}
            </Typography>
          </Paper>
        ))}
      </Box>

      {sessionsQuery.hasNextPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="outlined" onClick={() => void sessionsQuery.fetchNextPage()} disabled={sessionsQuery.isFetchingNextPage}>
            {t('stocktakeSessions.loadMoreSessions', { shown: sessions.length, total: sessionsQuery.data?.total ?? sessions.length })}
          </Button>
        </Box>
      )}

      <NewStocktakeDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={(session) => {
          setNewOpen(false);
          setSelectedId(session.id);
        }}
      />
    </Box>
  );
}
